import { useCallback } from 'react';
import { useRightClick } from '../rightClickProvider';
import { MenuItem } from 'components/dropdowns/RMDropdown';
import { IGNORE_AUTO_SCORE_MESSAGES } from 'constants/messages/ignoreAutoScore';

export const useExperimentRightClickOptions = () => {
  const { replaceMenuItems } = useRightClick();

  const getImageModeOptions = useCallback((actionMap): MenuItem[] => {
    return [
      actionMap['Edit Experiment'] && {
        label: 'Edit Experiment',
        onClick: actionMap['Edit Experiment']?.action,
        isDisabled: actionMap['Edit Experiment']?.isDisabled,
      },
      {
        label: 'Inspections',
        onClick: actionMap['Inspections']?.action,
        submenuItems: actionMap['Inspections']?.subMenu,
      },
      {
        label: 'Export',
        submenuItems: actionMap['Export']?.subMenu,
      },
      {
        label: 'Save Image',
        onClick: actionMap['Save Image']?.action,
      },
      {
        label: 'Reports',
        submenuItems: actionMap['Reports']?.subMenu,
      },
      {
        label: 'Drop Captures',
        submenuItems: actionMap['Drop Captures']?.subMenu,
        customSubmenuComponent: actionMap['Drop Captures']?.customSubmenuComponent,
      },
      {
        label: IGNORE_AUTO_SCORE_MESSAGES.label,
        onClick: actionMap[IGNORE_AUTO_SCORE_MESSAGES.label]?.action,
        isSelected: actionMap[IGNORE_AUTO_SCORE_MESSAGES.label]?.isSelected,
        isDisabled: actionMap[IGNORE_AUTO_SCORE_MESSAGES.label]?.isDisabled,
        shouldShowSelectedItemCheckmark: true,
      },
      {
        label: 'Disable Imaging',
        onClick: actionMap['Disable Image']?.action,
        isSelected: actionMap['Disable Image']?.isSelected,
        isDisabled: actionMap['Disable Image']?.isDisabled,
        shouldShowSelectedItemCheckmark: true,
      },
      {
        label: 'Enable Import Focus Level',
        onClick: actionMap['Enable Import Focus Level']?.action,
        isSelected: actionMap['Enable Import Focus Level']?.isSelected,
        isDisabled: actionMap['Enable Import Focus Level']?.isDisabled,
        shouldShowSelectedItemCheckmark: true,
      },
      {
        label: 'New Seed',
        onClick: actionMap['New Seed']?.action,
        isDisabled: actionMap['New Seed']?.isDisabled,
      },
      {
        label: 'Mark Interesting',
        onClick: actionMap['Mark Interesting']?.action,
        isSelected: actionMap['Mark Interesting']?.isSelected,
        shouldShowSelectedItemCheckmark: true,
      },
      {
        label: 'Copy',
        subLabel: 'Ctrl + C',
        onClick: actionMap['Copy']?.action,
        isDisabled: actionMap['Copy']?.isDisabled,
      },
      {
        label: 'Paste',
        subLabel: 'Ctrl + V',
        onClick: actionMap['Paste']?.action,
        isDisabled: actionMap['Paste']?.isDisabled,
      },
    ].filter(Boolean);
  }, []);

  const getDropWellCompareModeOptions = useCallback((actionMap): MenuItem[] => {
    return [
      {
        label: 'Save Image',
        onClick: actionMap['Save Image']?.action,
      },
      {
        label: IGNORE_AUTO_SCORE_MESSAGES.label,
        onClick: actionMap[IGNORE_AUTO_SCORE_MESSAGES.label]?.action,
        isSelected: actionMap[IGNORE_AUTO_SCORE_MESSAGES.label]?.isSelected,
        isDisabled: actionMap[IGNORE_AUTO_SCORE_MESSAGES.label]?.isDisabled,
        shouldShowSelectedItemCheckmark: true,
      },
      {
        label: 'Disable Imaging',
        onClick: actionMap['Disable Image']?.action,
        isSelected: actionMap['Disable Image']?.isSelected,
        shouldShowSelectedItemCheckmark: true,
      },
      {
        label: 'Enable Import Focus Level',
        onClick: actionMap['Enable Import Focus Level']?.action,
        isSelected: actionMap['Enable Import Focus Level']?.isSelected,
        shouldShowSelectedItemCheckmark: true,
      },
      {
        label: 'Select Focus Level',
        onClick: actionMap['Select Focus Level']?.action,
        submenuItems: actionMap['Select Focus Level']?.subMenu,
        isDisabled: actionMap['Select Focus Level']?.isDisabled,
      },
      {
        label: 'New Seed',
        onClick: actionMap['New Seed']?.action,
        isDisabled: actionMap['New Seed']?.isDisabled,
      },
      {
        label: 'Single Drop Report',
        onClick: actionMap['Single Drop Report']?.action,
        isDisabled: actionMap['Single Drop Report']?.isDisabled,
      },
    ];
  }, []);

  const getDesignModeOptions = useCallback((actionMap): MenuItem[] => {
    return [
      {
        label: 'Add Layer Tool',
        onClick: actionMap['Add Layer Tool']?.action,
        submenuItems: actionMap['Add Layer Tool']?.subMenu,
      },
      ...getIngredientScoreModeOptions(actionMap),
    ];
  }, []);

  const getIngredientScoreModeOptions = useCallback((actionMap): MenuItem[] => {
    return [
      actionMap['Edit Experiment'] && {
        label: 'Edit Experiment',
        onClick: actionMap['Edit Experiment']?.action,
        isDisabled: actionMap['Edit Experiment']?.isDisabled,
      },
      {
        label: 'Import XML Screen',
        onClick: actionMap['Import XML Screen']?.action,
        isDisabled: actionMap['Import XML Screen']?.isDisabled,
      },
      {
        label: 'Export',
        submenuItems: actionMap['Export']?.subMenu,
      },
      {
        label: 'Reports',
        submenuItems: actionMap['Reports']?.subMenu,
      },
      {
        label: 'New Seed',
        onClick: actionMap['New Seed']?.action,
        isDisabled: actionMap['New Seed']?.isDisabled,
      },
      {
        label: 'Mark as Dispensed',
        onClick: actionMap['Mark as Dispensed']?.action,
        isDisabled: actionMap['Mark as Dispensed']?.isDisabled,
      },
      {
        label: 'Copy',
        onClick: actionMap['Copy']?.action,
        isDisabled: actionMap['Copy']?.isDisabled,
        subLabel: 'Ctrl + C',
      },
      {
        label: 'Paste',
        subLabel: 'Ctrl + V',
        onClick: actionMap['Paste']?.action,
        isDisabled: actionMap['Paste']?.isDisabled,
      },
    ].filter(Boolean);
  }, []);

  return useCallback(
    (actionMap, displayMode) => {
      switch (displayMode) {
        case 'IMAGE':
          replaceMenuItems(getImageModeOptions(actionMap));
          break;
        case 'WELLIMAGE':
          replaceMenuItems(getDropWellCompareModeOptions(actionMap));
          break;
        case 'DESIGN':
          replaceMenuItems(getDesignModeOptions(actionMap));
          break;
        case 'WELLINGREDIENT':
          replaceMenuItems(getIngredientScoreModeOptions(actionMap));
          break;
        case 'SCORE':
          replaceMenuItems(getIngredientScoreModeOptions(actionMap));
          break;
        default:
          replaceMenuItems([]);
          break;
      }
    },
    [
      replaceMenuItems,
      getImageModeOptions,
      getDropWellCompareModeOptions,
      getDesignModeOptions,
      getIngredientScoreModeOptions,
    ],
  );
};
